import {AxiosRequestConfig} from 'axios';
import {CareTask} from '../backend/interfaces/care-task';
import {EntityShift} from '../backend/interfaces/entity-shift';
import {PostEntityShift} from '../backend/requests/post-entity-shift';
import {PutEntityShift} from '../backend/requests/put-entity-shift';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';
import {CareTaskComplementary} from './../backend/interfaces/care-task-complementary';
import {PaginatedArrayResponse} from './interfaces/paginated-array-response';

export class EntityShiftsRepository {
  // Singleton Instance
  private static _instance: EntityShiftsRepository;

  // Private
  private logger: Logger;

  static get instance(): EntityShiftsRepository {
    if (!this._instance) {
      this._instance = new EntityShiftsRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('EntityShiftsRepository');
  }

  // Get all shifts that a target entity has
  public async getShifts(
    entityId: string,
    limit: number,
    skip: number
  ): Promise<PaginatedArrayResponse<EntityShift>> {
    const response = await BackendApiManager.instance.get<EntityShift[]>(
      `/entities/${entityId}/shifts?limit=${limit}&skip=${skip}`
    );
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get an Entity Shift by Id
  public async getShift(entityId: string, entityShiftId: string): Promise<EntityShift> {
    const response = await BackendApiManager.instance.get<EntityShift>(
      `/entities/${entityId}/shifts/${entityShiftId}`
    );
    return response.data;
  }

  // Get an Entity Shift care tasks
  public async getShiftCareTasks(
    entityId: string,
    entityShiftId: string,
    startDate: string,
    endDate: string,
    includeComplementaryInformation?: boolean,
    config?: AxiosRequestConfig
  ): Promise<CareTask[] | CareTaskComplementary[]> {
    let url = `/entities/${entityId}/shifts/${entityShiftId}/care-tasks/?startDate=${startDate}&endDate=${endDate}`;

    if (includeComplementaryInformation) {
      url += '&includeComplementaryInformation=true';
    }

    url += '&localize=true';

    const response = await BackendApiManager.instance.get<CareTask[] | CareTaskComplementary[]>(
      url,
      config
    );

    return response.data;
  }

  // Creates a new entity shift
  public async createShift(entityId: string, shift: PostEntityShift): Promise<EntityShift> {
    const response = await BackendApiManager.instance.post<EntityShift>(
      `/entities/${entityId}/shifts`,
      shift
    );
    return response.data;
  }

  // Updates an entity shift
  public async updateShift(
    entityId: string,
    shiftId: string,
    shift: PutEntityShift
  ): Promise<EntityShift> {
    const response = await BackendApiManager.instance.put<EntityShift>(
      `/entities/${entityId}/shifts/${shiftId}`,
      shift
    );
    return response.data;
  }

  // Deletes an entity shift
  public async deleteShift(entityId: string, shiftId: string): Promise<void> {
    await BackendApiManager.instance.delete(`/entities/${entityId}/shifts/${shiftId}`);
  }
}
